import * as React from 'react';
import { connect } from 'react-redux';
import { find } from 'lodash';
import * as moment from 'moment';
import { Card, Tag, Icon, Tooltip } from '../';
import BookingItemControls from './BookingItemControls';
import bookingStatuses from '../../data/bookingStatuses';
import paymentStatuses from '../../data/paymentStatuses';

interface Props {
  booking: any;
  handleUpdate?: Function;
  currentDay?: any;
  hideControls?: boolean;
  link?: string | boolean;
  vehicleTypes: Array<any>;
}

/**
 * A singular booking item used for archive views
 */
const BookingItem = ({
  booking,
  handleUpdate = () => {},
  currentDay = moment(),
  vehicleTypes,
  hideControls = false,
  link = false,
}: Props) => {
  const start = moment(booking.start).format('DD/MM/YY [at] HH:mm');
  const end = moment(booking.end).format('DD/MM/YY');
  let returned = null;
  if (booking.return_date) {
    returned = moment(booking.return_date).format('DD/MM/YY');
  }

  // Determine if hire ends today
  let endsToday = false;
  if (['ON-HOLD', 'COMPLETE'].indexOf(booking.status) === -1) {
    endsToday =
      moment(booking.end).format('YYYY-MM-DD') ===
      currentDay.format('YYYY-MM-DD');
  }

  // Determine vehicle type
  let vehicleType = booking.vehicle_type;
  if (!booking.vehicle_type || !booking.vehicle_type.image) {
    vehicleType = find(vehicleTypes, { id: booking.vehicle_type_id });
  }

  const bookingStatus = find(bookingStatuses, { value: booking.status });
  let paymentStatus: any = false;
  if (['UNASSIGNED', 'ACTIVE'].indexOf(booking.status) === -1) {
    paymentStatus = find(paymentStatuses, { value: booking.payment_status });
  }

  const duration = 1 + moment(booking.end).diff(moment(booking.start), 'd');

  return (
    <Card
      list
      key={booking.id}
      link={link}
      className={`card--${bookingStatus.class}`}
    >
      <article className="media">
        <figure className="media-left has-text-centered">
          <span className="avatar image is-64x64">
            <img src={vehicleType.image} />
          </span>
          {booking.vehicle && (
            <Tag type="warning" className="has-text-weight-bold">
              {booking.vehicle.registration}
            </Tag>
          )}
        </figure>
        <div className="media-content">
          <div className="content">
            <small className="has-text-grey">
              {start} - {end}
              {!returned ? (
                endsToday && (
                  <>
                    {' '}
                    <Tooltip text="Due back today">
                      <span className="has-text-danger">
                        <Icon icon="exclamation-triangle" />
                      </span>
                    </Tooltip>
                  </>
                )
              ) : (
                <>
                  {' '}
                  <Tooltip text={`Returned on ${returned}`}>
                    <span className="has-text-success">
                      <Icon icon="check" />
                    </span>
                  </Tooltip>
                </>
              )}
            </small>
            <br />
            {/* <Tag type="dark">{booking.invoiceId}</Tag>{' '} */}
            <strong>
              {booking.contact_name || (
                <span className="has-text-danger">Unknown Contact</span>
              )}
            </strong>
            <br />
            {vehicleType.name} Booking for {duration} day{duration !== 1 && 's'}
            {!!booking.booking_accessories &&
              !!booking.booking_accessories.length && (
                <>
                  <br />
                  <small>
                    <span className="has-text-grey-light">
                      <Tooltip text="Accessories">
                        <Icon icon="cubes" />
                      </Tooltip>
                    </span>{' '}
                    {booking.booking_accessories
                      .map(
                        (accessory) =>
                          accessory.accessory &&
                            `${accessory.quantity} × ${accessory.accessory.name}`
                      )
                      .join(', ')}
                  </small>
                </>
              )}
            {!!booking.notes &&
              !!booking.notes.length && (
                <>
                  <br />
                  <small>
                    <span className="has-text-grey-light">
                      <Tooltip text="Notes">
                        <Icon icon="comments" />
                      </Tooltip>
                    </span>{' '}
                    {booking.notes.map((note) => note.text).join(', ')}
                  </small>
                </>
              )}
          </div>
          {!hideControls && (
            <BookingItemControls
              booking={booking}
              handleUpdate={handleUpdate}
            />
          )}
        </div>
        <div className="media-right">
          <div className="field is-grouped is-grouped-right">
            <span className="control">
              <Tooltip text={bookingStatus.tooltip}>
                <Tag type={bookingStatus.class}>
                  {bookingStatus.name.toUpperCase()}
                </Tag>
              </Tooltip>
            </span>
            {paymentStatus !== false &&
              booking.status !== 'CANCELLED' && (
                <span className="control">
                  <Tooltip text={paymentStatus.tooltip}>
                    <Tag type={paymentStatus.class}>
                      {paymentStatus.name.toUpperCase()}
                    </Tag>
                  </Tooltip>
                </span>
              )}
            <span className="control">
              <Tooltip text="Invoice number">
                <Tag type="dark">{booking.invoiceId}</Tag>
              </Tooltip>
            </span>
          </div>
        </div>
      </article>
    </Card>
  );
};

const mapStateToProps = ({ vehicleTypes }) => ({
  vehicleTypes: vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(BookingItem);
